import { useEffect } from 'react';
import { useTalentsStore } from '@libs/store/talents';
import { PrimaryPaper } from '@atoms/layout';
import {
  useShiftAddEditStore,
  useShiftsListStore,
  useShiftSeriesAddEditStore,
  useShiftRotationAddEditStore,
  usePublishDrawerStore,
  useShareDrawerStore,
  useHardDeleteModalStore,
  useShiftSeriesEditStore,
  useRotationSeriesEditStore,
} from '@libs/store/shifts';
import { EditShiftDrawer, ShareShifts, EditSeriesDrawer, EditRotationDrawer, PublishShiftsDrawer } from '../../drawers';
import { useOrgQualificationsStore } from '@libs/store/settings';
import { useLocationStore } from '@libs/store/locations';
import { useRefreshSyncStore } from '@libs/store/refreshSync';
import { ShiftHardDeleteDialog } from '../../dialogs';
import { RotationDetailsDrawer, SeriesDetailsDrawer, ShiftDetailsDrawer } from '../../drawers/shiftDetails';
import { ShiftsTable } from './table';
import { ShiftTableMenu } from '../shiftTableMenu';
import { ShiftsFilters } from './shiftsFilters';
import { Box, Stack } from '@mui/material';

export function ShiftsListPage() {
  const { callRefresh } = useRefreshSyncStore();
  const { initiated: talentsListInitiated, load } = useTalentsStore();
  const { initiated: shiftsListInitiated } = useShiftsListStore();
  const { initiated: qualListInitiated } = useOrgQualificationsStore();
  const { currentInitiated: currentLocationInitiated } = useLocationStore();

  const loading = !shiftsListInitiated || !qualListInitiated || !currentLocationInitiated || !talentsListInitiated;

  useEffect(() => {
    callRefresh();
  }, [callRefresh]);

  useEffect(() => {
    // eslint-disable-next-line @typescript-eslint/no-floating-promises
    load({});
  }, [load]);

  return (
    <>
      <ShiftTableMenu loading={loading} />
      <PrimaryPaper sx={{ height: '100%', minHeight: 0, mt: 2 }}>
        <Stack direction="column" height="100%">
          <ShiftsFilters loading={loading} />
          <Box mt={2} height="100%" minHeight={0}>
            <ShiftsTable />
          </Box>
        </Stack>
      </PrimaryPaper>
      <Drawers />
    </>
  );
}

function Drawers() {
  const { open: hardDeletePopupOpen } = useHardDeleteModalStore();
  const { drawerOpen: seriesDrawerOpen } = useShiftSeriesAddEditStore();
  const { drawerOpen: rotationDrawerOpen } = useShiftRotationAddEditStore();
  const { drawerOpen: shareDrawerOpen } = useShareDrawerStore();
  const { drawerOpen: publishDrawerOpen } = usePublishDrawerStore();
  const { drawerOpen: seriesDetailsDrawerOpen } = useShiftSeriesEditStore();
  const { drawerOpen: rotationDetailsDrawerOpen } = useRotationSeriesEditStore();
  const openShiftDrawer = useShiftAddEditStore((s) => s.openShiftDrawer);
  const closeSingleShiftDrawer = useShiftAddEditStore((s) => s.closeShiftDrawer);
  const { callRefresh } = useRefreshSyncStore();
  const timezone = useLocationStore((s) => s.current?.timezone);

  const handleListPossiblyChanged = () => {
    callRefresh();
  };

  useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.ctrlKey && event.key === 'v') {
        navigator.clipboard
          .readText()
          .then((text) => {
            const data = JSON.parse(text);
            if (data) {
              event.preventDefault();
            }
            if (
              data.dataType &&
              data.dataType === 'singleShift' &&
              !seriesDrawerOpen &&
              !rotationDrawerOpen &&
              !shareDrawerOpen &&
              !publishDrawerOpen
            ) {
              openShiftDrawer(data.id, data, 'copy');
            }
          })
          .catch((e) => {
            console.warn('Can`t paste shift from clipboard', e);
          });
      }
    };

    document.addEventListener('keydown', handleKeyDown);
    return () => {
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, []);

  return (
    <>
      {hardDeletePopupOpen && (
        <ShiftHardDeleteDialog
          afterSubmit={() => {
            handleListPossiblyChanged();
            closeSingleShiftDrawer();
          }}
        />
      )}
      <ShiftDetailsDrawer />
      <EditShiftDrawer onSaveCall={handleListPossiblyChanged} />
      {seriesDetailsDrawerOpen && <SeriesDetailsDrawer onSaved={handleListPossiblyChanged} />}
      {rotationDetailsDrawerOpen && <RotationDetailsDrawer onSaved={handleListPossiblyChanged} />}
      {seriesDrawerOpen && <EditSeriesDrawer onSaveCall={handleListPossiblyChanged} />}
      {rotationDrawerOpen && <EditRotationDrawer onSaveCall={handleListPossiblyChanged} />}
      {publishDrawerOpen && <PublishShiftsDrawer onSaveCall={handleListPossiblyChanged} />}
      {shareDrawerOpen && <ShareShifts onSaveCall={handleListPossiblyChanged} />}
    </>
  );
}
